import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';
import { ArrowLeft, Plus, Trash2, LayoutGrid, Save, Send, Loader2, AlertTriangle } from 'lucide-react';
import { ProductGridSelector } from '@/components/shared/ProductGridSelector';
import { useAuthStore } from '@/stores/auth';
import { BillShipOverride, type BillShipValue } from './BillShipOverride';
import { joinPersonName } from '@/utils/names';
import { addressFieldsFromJson } from './soAddressFill';
import {
  additionalDiscountNotAllowedText,
  factsFromLinePricing,
  lineDiscountVerdict,
  lineRefusedMessage,
  orderDiscountLimitNote,
  orderDiscountOverLimitMessage,
  resolveAdditionalAllowanceCeiling,
  type LineFacts,
} from './orderDiscountRules';

const GST_RATES = [0, 5, 12, 18, 28];
const FULFILLMENT_SOURCES = [
  { value: 'in_stock', label: 'In Stock' },
  { value: 'procure', label: 'Procure' },
  { value: 'manufacture', label: 'Manufacture' },
  { value: 'service', label: 'Service' },
];

const STATES = [
  { code: '01', name: 'Jammu & Kashmir' }, { code: '02', name: 'Himachal Pradesh' },
  { code: '03', name: 'Punjab' }, { code: '04', name: 'Chandigarh' },
  { code: '05', name: 'Uttarakhand' }, { code: '06', name: 'Haryana' },
  { code: '07', name: 'Delhi' }, { code: '08', name: 'Rajasthan' },
  { code: '09', name: 'Uttar Pradesh' }, { code: '10', name: 'Bihar' },
  { code: '11', name: 'Sikkim' }, { code: '12', name: 'Arunachal Pradesh' },
  { code: '13', name: 'Nagaland' }, { code: '14', name: 'Manipur' },
  { code: '15', name: 'Mizoram' }, { code: '16', name: 'Tripura' },
  { code: '17', name: 'Meghalaya' }, { code: '18', name: 'Assam' },
  { code: '19', name: 'West Bengal' }, { code: '20', name: 'Jharkhand' },
  { code: '21', name: 'Odisha' }, { code: '22', name: 'Chhattisgarh' },
  { code: '23', name: 'Madhya Pradesh' }, { code: '24', name: 'Gujarat' },
  { code: '25', name: 'Daman & Diu' }, { code: '26', name: 'Dadra & Nagar Haveli' },
  { code: '27', name: 'Maharashtra' }, { code: '29', name: 'Karnataka' },
  { code: '30', name: 'Goa' }, { code: '31', name: 'Lakshadweep' },
  { code: '32', name: 'Kerala' }, { code: '33', name: 'Tamil Nadu' },
  { code: '34', name: 'Puducherry' }, { code: '35', name: 'Andaman & Nicobar' },
  { code: '36', name: 'Telangana' }, { code: '37', name: 'Andhra Pradesh' },
];

interface LineItem {
  description: string;
  hsnSac: string;
  quantity: number;
  rate: number;
  gstRate: number;
  discount: number;
  maxDiscount: number | null;
  discountApprovalEnabled?: boolean;
  productId?: string;
  fulfillmentSource: string;
  unit?: string;
  gstMode?: 'extra' | 'inclusive' | null;
}

export function SalesOrderEditorPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const fromEstimateId = searchParams.get('fromEstimate');
  const isNew = !id || id === 'new';
  const tenant = useAuthStore((s) => s.tenant);
  // Extended commodity/trading fields — gate by module flag, never by tenant slug (D-09).
  // enabledModules === null means platform/super-admin → show everything.
  const showCommodityFields =
    tenant?.enabledModules == null || tenant.enabledModules.includes('commodity_trading');

  const [contacts, setContacts] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [showGrid, setShowGrid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew || !!fromEstimateId);
  const [error, setError] = useState<string | null>(null);
  // 🔒 Manak 2026-10-03 — per-product facts from /proposals/line-pricing: the
  // rep's self-serve, the product's own max, the order-discount limit and
  // whether the category allows an order discount at all.
  const [lineFacts, setLineFacts] = useState<Record<string, LineFacts>>({});

  const [form, setForm] = useState({
    contactId: '', accountId: '', deliveryDate: '', buyerGstin: '', buyerState: '',
    customerType: 'b2c' as string,
    notes: '', terms: '', internalNotes: '', isExport: false, exportCountry: '',
    deliveryTerms: '', currency: 'INR',
    billingAddress: { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' },
    shippingAddress: { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' },
    // Veda Agro fields
    commodity: '', salesTeamMember: '', confirmed: false, phase: 'lead',
    commodityCost: '', logisticsCost: '', margin: '', marginPct: '',
  });
  const [items, setItems] = useState<LineItem[]>([
    { description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, maxDiscount: null, fulfillmentSource: 'in_stock' },
  ]);

  // Document-level discount
  const [docDiscountType, setDocDiscountType] = useState<'amount' | 'percentage'>('amount');
  const [docDiscountValue, setDocDiscountValue] = useState<number>(0);
  const [maxDocDiscountPct, setMaxDocDiscountPct] = useState<number>(100);

  // Document GST mode
  const [docGstMode, setDocGstMode] = useState<'extra' | 'inclusive'>('extra');

  // Bill-to / ship-to override blocks (only included in the save payload when ticked)
  const [billShip, setBillShip] = useState<BillShipValue>({});

  useEffect(() => {
    api.get('/contacts?limit=100').then(r => setContacts(r.data.data?.contacts ?? r.data.data ?? [])).catch(() => {});
    api.get('/accounts?type=customer&limit=100').then(r => setAccounts(r.data.data?.accounts ?? r.data.data ?? [])).catch(() => {});
    api.get('/settings/discount-policy').then(res => {
      const policy = res.data?.data;
      if (policy?.maxDocumentDiscountPct) setMaxDocDiscountPct(policy.maxDocumentDiscountPct);
      if (policy?.defaultDiscountInputMode) setDocDiscountType(policy.defaultDiscountInputMode as any);
    }).catch(() => {});
  }, []);

  // Load existing SO in edit mode
  useEffect(() => {
    if (!isNew && id) {
      setLoading(true);
      api.get(`/sales-orders/${id}`)
        .then(res => {
          const so = res.data.data;
          setForm(prev => ({
            ...prev,
            contactId: so.contactId || '',
            accountId: so.accountId || '',
            deliveryDate: so.deliveryDate ? so.deliveryDate.split('T')[0] : '',
            buyerGstin: so.buyerGstin || '',
            buyerState: so.buyerState || '',
            notes: so.notes || '',
            terms: so.terms || '',
            internalNotes: so.internalNotes || '',
            isExport: so.isExport || false,
            exportCountry: so.exportCountry || '',
            deliveryTerms: so.deliveryTerms || '',
            currency: so.currency || 'INR',
            billingAddress: so.billingAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' },
            shippingAddress: so.shippingAddress || { line1: '', line2: '', city: '', state: '', pincode: '', country: 'India' },
            commodity: so.commodity || '',
            salesTeamMember: so.salesTeamMember || '',
            confirmed: so.confirmed || false,
            phase: so.phase || 'lead',
            commodityCost: so.commodityCost || '',
            logisticsCost: so.logisticsCost || '',
            margin: so.margin || '',
            marginPct: so.marginPct || '',
          }));
          // Load document-level discount
          if (so.discountType) setDocDiscountType(so.discountType);
          if (so.discountValue != null) setDocDiscountValue(Number(so.discountValue));
          if (so.gstMode) setDocGstMode(so.gstMode as 'extra' | 'inclusive');
          if (so.items?.length) {
            setItems(so.items.map((i: any) => ({
              description: i.description,
              hsnSac: i.hsnSac || '',
              quantity: Number(i.quantity),
              rate: Number(i.rate),
              gstRate: Number(i.gstRate),
              discount: Number(i.discount) || 0,
              maxDiscount: i.maxDiscount ?? null,
              discountApprovalEnabled: i.discountApprovalEnabled !== false,
              productId: i.productId,
              fulfillmentSource: i.fulfillmentSource || 'in_stock',
              unit: i.unit || '',
            })));
          }
        })
        .catch(() => setError('Failed to load sales order'))
        .finally(() => setLoading(false));
    }
  }, [id, isNew]);

  // Pre-fill from estimate
  useEffect(() => {
    if (!fromEstimateId) return;
    setLoading(true);
    api.get(`/estimates/${fromEstimateId}`)
      .then(res => {
        const est = res.data.data;
        setForm(prev => ({
          ...prev,
          contactId: est.contactId || '',
          accountId: est.accountId || '',
          buyerGstin: est.buyerGstin || '',
          buyerState: est.placeOfSupply || '',
          notes: est.notes || '',
          terms: est.terms || '',
        }));
        if (est.items?.length) {
          setItems(est.items.map((i: any) => ({
            description: i.description,
            hsnSac: i.hsnSac || '',
            quantity: Number(i.quantity),
            rate: Number(i.rate),
            gstRate: Number(i.gstRate),
            discount: Number(i.discount) || 0,
            maxDiscount: i.maxDiscount ?? null,
            productId: i.productId,
            fulfillmentSource: 'in_stock',
            unit: '',
          })));
        }
      })
      .catch(() => setError('Failed to load quote'))
      .finally(() => setLoading(false));
  }, [fromEstimateId]);

  // GST Calculation
  const sellerState = '06'; // Haryana — loaded from company profile in production
  const buyerState = form.buyerState || sellerState;
  const isIntraState = sellerState === buyerState && !form.isExport;

  // Document ("additional") discount REDUCES the GST taxable base (applied per-line before GST).
  const calculations = useMemo(() => {
    const round2 = (n: number) => Math.round(n * 100) / 100;

    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;
    let totalItemDiscount = 0;

    // Pass 1: line taxables (after per-line discount) → Subtotal (pre-doc-discount).
    const lineTaxables = items.map(item => {
      const grossAmount = item.quantity * item.rate;
      const discountAmount = (grossAmount * (item.discount || 0)) / 100;
      const taxableAmount = round2(grossAmount - discountAmount);
      return { grossAmount, discountAmount, taxableAmount };
    });
    const subtotal = round2(lineTaxables.reduce((s, l) => s + l.taxableAmount, 0));

    // Document-level discount — known before per-line GST so it can reduce the taxable base.
    let docDiscountAmt = 0;
    let docDiscountPct = 0;
    if (docDiscountType === 'percentage') {
      docDiscountPct = Math.min(Math.max(docDiscountValue, 0), 100);
      docDiscountAmt = round2(subtotal * docDiscountPct / 100);
    } else {
      docDiscountAmt = round2(Math.min(docDiscountValue, subtotal));
      docDiscountPct = subtotal > 0 ? (docDiscountValue / subtotal) * 100 : 0;
    }
    const afterDocDiscount = subtotal - docDiscountAmt;
    const docFactor = 1 - docDiscountPct / 100;

    // Pass 2: per-line GST on the doc-discount-REDUCED taxable base.
    const itemCalcs = items.map((item, idx) => {
      const { grossAmount, discountAmount, taxableAmount } = lineTaxables[idx];
      const effectiveGstRate = form.isExport ? 0 : item.gstRate;
      const gstAmount = (taxableAmount * docFactor * effectiveGstRate) / 100;
      let cgst = 0, sgst = 0, igst = 0;

      if (form.isExport) {
        // Export: zero-rated
      } else if (buyerState && isIntraState) {
        cgst = gstAmount / 2;
        sgst = gstAmount / 2;
      } else {
        igst = gstAmount;
      }

      totalCgst += cgst;
      totalSgst += sgst;
      totalIgst += igst;
      totalItemDiscount += discountAmount;

      return { grossAmount, discountAmount, taxableAmount, cgst, sgst, igst, total: taxableAmount + gstAmount };
    });

    const totalTax = round2(totalCgst + totalSgst + totalIgst);
    const grandTotal = Math.round((afterDocDiscount + totalTax) * 100) / 100;

    return { subtotal, totalCgst: round2(totalCgst), totalSgst: round2(totalSgst), totalIgst: round2(totalIgst), totalTax, grandTotal, itemCalcs, totalItemDiscount, docDiscountAmt, docDiscountPct, afterDocDiscount };
  }, [items, buyerState, isIntraState, form.isExport, docDiscountType, docDiscountValue]);

  // 🔒 Manak 2026-10-03 — the same /proposals/line-pricing read the proposal
  // editor and the phone use. Best-effort: the server gate decides on save.
  const productKey = useMemo(
    () => Array.from(new Set(items.map(i => i.productId).filter(Boolean) as string[])).sort().join(','),
    [items],
  );
  useEffect(() => {
    const ids = productKey ? productKey.split(',') : [];
    if (ids.length === 0) return;
    let cancelled = false;
    api.post('/proposals/line-pricing', { productIds: ids, customerType: form.customerType })
      .then(res => {
        if (cancelled) return;
        const facts = factsFromLinePricing(res.data?.data ?? {});
        setLineFacts(facts);
        // The wall is the product's OWN max (never slab 2).
        setItems(prev => prev.map(li => {
          const f = li.productId ? facts[li.productId] : undefined;
          if (!f) return li;
          const nextMax = f.discountApprovalEnabled === false ? null : (f.productMaxDiscountPct ?? li.maxDiscount);
          if (li.maxDiscount === nextMax && li.discountApprovalEnabled === f.discountApprovalEnabled) return li;
          return { ...li, maxDiscount: nextMax, discountApprovalEnabled: f.discountApprovalEnabled };
        }));
      })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, [productKey, form.customerType]);

  const namedItems = useMemo(() => items.filter(i => i.description.trim() !== ''), [items]);
  const orderDiscountLimitPct = useMemo(
    () => resolveAdditionalAllowanceCeiling(namedItems.map(i => i.productId), lineFacts),
    [namedItems, lineFacts],
  );
  const orderDiscountNotAllowed = useMemo(
    () => additionalDiscountNotAllowedText(namedItems.map(i => ({ productId: i.productId, name: i.description })), lineFacts),
    [namedItems, lineFacts],
  );
  const lineVerdict = (item: LineItem) =>
    lineDiscountVerdict(item.discount, item.productId ? {
      ...(lineFacts[item.productId] ?? { autoApprovePct: null, productMaxDiscountPct: null }),
      productMaxDiscountPct: item.maxDiscount,
      discountApprovalEnabled: item.discountApprovalEnabled,
    } : (item.maxDiscount != null ? { autoApprovePct: null, productMaxDiscountPct: item.maxDiscount, discountApprovalEnabled: item.discountApprovalEnabled } : null));

  const addItem = () => {
    setItems([...items, { description: '', hsnSac: '', quantity: 1, rate: 0, gstRate: 18, discount: 0, maxDiscount: null, fulfillmentSource: 'in_stock' }]);
  };

  const removeItem = (idx: number) => {
    if (items.length <= 1) return;
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: keyof LineItem, value: any) => {
    setItems(items.map((item, i) => i === idx ? { ...item, [field]: value } : item));
  };

  const handleProductSelect = (product: any) => {
    setItems(prev => [
      ...prev,
      {
        description: product.name,
        hsnSac: product.hsnCode || '',
        quantity: 1,
        rate: product.unitPrice,
        gstRate: product.gstRate,
        discount: 0,
        maxDiscount: product.maxDiscount ?? null,
        productId: product.id,
        fulfillmentSource: 'in_stock',
        unit: product.unit || '',
      },
    ]);
    setShowGrid(false);
  };

  const handleSave = async (andConfirm = false) => {
    setSaving(true);
    setError(null);
    try {
      // ── 🔒 Manak 2026-10-03 — the SAME gate as the phone / proposal editor ──
      // ORDER (additional) discount: no approval step (ruling 2026-08-27) —
      // within the order's limit it is allowed, above it REFUSED.
      const effDocDiscountPct = calculations.docDiscountPct ?? 0;
      if (effDocDiscountPct > 0) {
        if (orderDiscountNotAllowed) {
          setError(`${orderDiscountNotAllowed} — remove the order discount to save.`);
          setSaving(false);
          return;
        }
        const limit = Math.min(orderDiscountLimitPct, maxDocDiscountPct);
        if (effDocDiscountPct > limit + 1e-9) {
          setError(orderDiscountOverLimitMessage(effDocDiscountPct, limit));
          setSaving(false);
          return;
        }
      }

      // LINE discount: above self-serve SAVES (the server holds the order and
      // raises the approval request to the team head / MD); only above the
      // product's OWN maximum is it refused — nobody can clear that.
      const refusedItems = namedItems.filter(li => lineVerdict(li) === 'refused');
      if (refusedItems.length > 0) {
        setError(lineRefusedMessage(refusedItems.map(li => ({ name: li.description, pct: li.discount, maxPct: li.maxDiscount as number }))));
        setSaving(false);
        return;
      }

      const payload = {
        ...form,
        gstMode: docGstMode,
        discountType: docDiscountType,
        discountValue: docDiscountValue,
        discountPct: calculations.docDiscountPct,
        discountAmount: calculations.docDiscountAmt,
        items: items.filter(i => i.description.trim()).map(i => ({
          ...i,
          discount: i.discount || 0,
          gstMode: (i as any).gstMode ?? docGstMode,
        })),
        // Bill-to / ship-to override blocks — omitted entirely when unticked
        // (backend treats absence as no-override; never send useOverride:false).
        ...(billShip.billTo ? { billTo: billShip.billTo } : {}),
        ...(billShip.shipTo ? { shipTo: billShip.shipTo } : {}),
      };

      let soId = id;
      if (isNew) {
        if (fromEstimateId) {
          // Use from-estimate endpoint
          const res = await api.post(`/sales-orders/from-estimate/${fromEstimateId}`);
          soId = res.data.data.id;
        } else {
          const res = await api.post('/sales-orders', payload);
          soId = res.data.data.id;
        }
      } else {
        await api.put(`/sales-orders/${id}`, payload);
        soId = id;
      }

      if (andConfirm && soId) {
        try {
          await api.patch(`/sales-orders/${soId}/confirm`);
        } catch (confirmErr: any) {
          if (confirmErr?.response?.status === 422 && confirmErr?.response?.data?.error === 'mixed_gst_mode') {
            const d = confirmErr.response.data;
            setError(`Mixed GST mode detected: ${d.inclusiveCount} inclusive line(s) and ${d.extraCount} extra line(s). Please resolve GST modes before confirming.`);
            setSaving(false);
            return;
          }
          throw confirmErr;
        }
      }

      navigate(soId ? `/sales-orders/${soId}` : '/sales-orders');
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading...</span>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl">
      {/* Header */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={() => navigate('/sales-orders')} className="rounded-lg p-1.5 hover:bg-gray-100">
          <ArrowLeft size={20} className="text-gray-600" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{isNew ? 'New Sales Order' : 'Edit Sales Order'}</h1>
          {fromEstimateId && <p className="text-xs text-[var(--ink-0)]">Converting from Quote</p>}
        </div>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <div className="flex-1">
              <p>{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="space-y-6">
        {/* Customer Info */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">Customer Details</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Contact</label>
              <select value={form.contactId} onChange={e => {
                  const cid = e.target.value;
                  setForm(prev => ({ ...prev, contactId: cid }));
                  if (cid) {
                    api.get(`/contacts/${cid}`).then(res => {
                      const c = res.data?.data || res.data;
                      setForm(prev => {
                        const updates: any = { ...prev, contactId: cid };
                        // Auto-fill customer type from contact
                        if (c?.customerType) updates.customerType = c.customerType;
                        // Auto-fill account if contact has one
                        if (c?.accountId && !prev.accountId) updates.accountId = c.accountId;
                        // Auto-fill GSTIN from account
                        if (c?.account?.gstin && !prev.buyerGstin) updates.buyerGstin = c.account.gstin;
                        // Auto-fill addresses from account if available
                        // 2026-10-03 — Contact.address / Account.*Address are Json
                        // (string OR object). An object used to land in line1 as
                        // "[object Object]"; addressFieldsFromJson reads both shapes.
                        const ba = addressFieldsFromJson(c?.account?.billingAddress) ?? addressFieldsFromJson(c?.address);
                        if (ba && !prev.billingAddress?.line1) {
                          updates.billingAddress = { ...prev.billingAddress, ...ba };
                        }
                        const sa = addressFieldsFromJson(c?.account?.shippingAddress);
                        if (sa && !prev.shippingAddress?.line1) {
                          updates.shippingAddress = { ...prev.shippingAddress, ...sa };
                        }
                        return updates;
                      });
                    }).catch(() => {});
                  }
                }}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                <option value="">Select Contact</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{joinPersonName(c.firstName, c.lastName)}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Account</label>
              <select value={form.accountId} onChange={e => setForm({ ...form, accountId: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                <option value="">Select Account</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Delivery Date</label>
              <input type="date" value={form.deliveryDate} onChange={e => setForm({ ...form, deliveryDate: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Customer Type</label>
              <select value={form.customerType} onChange={e => setForm({ ...form, customerType: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                <option value="b2c">B2C — Individual</option>
                <option value="b2b">B2B — Business</option>
              </select>
            </div>
          </div>
        </div>

        {/* Addresses */}
        <div className="rounded-xl border border-[var(--border-primary)] bg-[var(--bg-surface)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--text-muted)] uppercase tracking-wider">Addresses</h2>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Billing Address */}
            <div>
              <h3 className="mb-2 text-xs font-medium text-[var(--text-secondary)]">Billing Address</h3>
              <div className="space-y-2">
                <input type="text" placeholder="Address Line 1" value={form.billingAddress?.line1 || ''} onChange={e => setForm({...form, billingAddress: {...form.billingAddress, line1: e.target.value}})}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                <input type="text" placeholder="Address Line 2" value={form.billingAddress?.line2 || ''} onChange={e => setForm({...form, billingAddress: {...form.billingAddress, line2: e.target.value}})}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                <div className="grid grid-cols-3 gap-2">
                  <input type="text" placeholder="City" value={form.billingAddress?.city || ''} onChange={e => setForm({...form, billingAddress: {...form.billingAddress, city: e.target.value}})}
                    className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                  <input type="text" placeholder="State" value={form.billingAddress?.state || ''} onChange={e => setForm({...form, billingAddress: {...form.billingAddress, state: e.target.value}})}
                    className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                  <input type="text" placeholder="Pincode" value={form.billingAddress?.pincode || ''} onChange={e => setForm({...form, billingAddress: {...form.billingAddress, pincode: e.target.value}})}
                    className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                </div>
                <input type="text" placeholder="Country" value={form.billingAddress?.country || 'India'} onChange={e => setForm({...form, billingAddress: {...form.billingAddress, country: e.target.value}})}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
              </div>
            </div>
            {/* Shipping / Delivery Address */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-xs font-medium text-[var(--text-secondary)]">Shipping / Delivery Address</h3>
                <button type="button" onClick={() => setForm({...form, shippingAddress: {...form.billingAddress}})}
                  className="text-[10px] text-[var(--ink-0)] hover:underline">Copy from Billing</button>
              </div>
              <div className="space-y-2">
                <input type="text" placeholder="Address Line 1" value={form.shippingAddress?.line1 || ''} onChange={e => setForm({...form, shippingAddress: {...form.shippingAddress, line1: e.target.value}})}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                <input type="text" placeholder="Address Line 2" value={form.shippingAddress?.line2 || ''} onChange={e => setForm({...form, shippingAddress: {...form.shippingAddress, line2: e.target.value}})}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                <div className="grid grid-cols-3 gap-2">
                  <input type="text" placeholder="City" value={form.shippingAddress?.city || ''} onChange={e => setForm({...form, shippingAddress: {...form.shippingAddress, city: e.target.value}})}
                    className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                  <input type="text" placeholder="State" value={form.shippingAddress?.state || ''} onChange={e => setForm({...form, shippingAddress: {...form.shippingAddress, state: e.target.value}})}
                    className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                  <input type="text" placeholder="Pincode" value={form.shippingAddress?.pincode || ''} onChange={e => setForm({...form, shippingAddress: {...form.shippingAddress, pincode: e.target.value}})}
                    className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
                </div>
                <input type="text" placeholder="Country" value={form.shippingAddress?.country || 'India'} onChange={e => setForm({...form, shippingAddress: {...form.shippingAddress, country: e.target.value}})}
                  className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-surface)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none" />
              </div>
            </div>
          </div>
        </div>

        {/* GST & Export */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">GST & Export</h2>
            {/* Document-level GST mode toggle */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">GST Mode:</span>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium">
                <button
                  type="button"
                  onClick={() => setDocGstMode('extra')}
                  className={`px-3 py-1.5 transition-colors ${docGstMode === 'extra' ? 'bg-[var(--saffron,#f59e0b)] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Extra (+GST)
                </button>
                <button
                  type="button"
                  onClick={() => setDocGstMode('inclusive')}
                  className={`px-3 py-1.5 border-l border-gray-200 transition-colors ${docGstMode === 'inclusive' ? 'bg-[var(--saffron,#f59e0b)] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  Inclusive
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Buyer GSTIN</label>
              <input type="text" value={form.buyerGstin} onChange={e => setForm({ ...form, buyerGstin: e.target.value })}
                placeholder="22AAAAA0000A1Z5"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Buyer State</label>
              <select value={form.buyerState} onChange={e => setForm({ ...form, buyerState: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                <option value="">Same as Seller</option>
                {STATES.map(s => <option key={s.code} value={s.code}>{s.code} - {s.name}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input type="checkbox" checked={form.isExport} onChange={e => setForm({ ...form, isExport: e.target.checked })}
                  className="rounded border-gray-300 text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                Export Order
              </label>
            </div>
            {form.isExport && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">Export Country</label>
                  <input type="text" value={form.exportCountry} onChange={e => setForm({ ...form, exportCountry: e.target.value })}
                    placeholder="AE, US, UK..."
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">Delivery Terms</label>
                  <select value={form.deliveryTerms} onChange={e => setForm({ ...form, deliveryTerms: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="">Select</option>
                    <option value="FOB">FOB (Free on Board)</option>
                    <option value="CIF">CIF (Cost, Insurance, Freight)</option>
                    <option value="EXW">EXW (Ex Works)</option>
                    <option value="CFR">CFR (Cost and Freight)</option>
                    <option value="DDP">DDP (Delivered Duty Paid)</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">Currency</label>
                  <select value={form.currency} onChange={e => setForm({ ...form, currency: e.target.value })}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="AED">AED</option>
                  </select>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Line Items */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Line Items</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowGrid(true)}
                className="flex items-center gap-1.5 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] px-3 py-1.5 text-xs font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)] transition-colors">
                <LayoutGrid size={14} />
                Product Grid
              </button>
              <button onClick={addItem}
                className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                <Plus size={14} />
                Add Row
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  <th className="px-3 py-2 w-[170px]">Description</th>
                  <th className="px-3 py-2 w-[75px]">HSN/SAC</th>
                  <th className="px-3 py-2 w-[55px] text-right">Qty</th>
                  <th className="px-3 py-2 w-[75px] text-right">Rate</th>
                  <th className="px-3 py-2 w-[65px] text-center">Disc %</th>
                  <th className="px-3 py-2 w-[65px] text-center">GST %</th>
                  <th className="px-3 py-2 w-[80px]">Source</th>
                  <th className="px-3 py-2 w-[65px] text-center">GST Mode</th>
                  {!form.isExport && form.buyerState && isIntraState && (
                    <>
                      <th className="px-3 py-2 w-[75px] text-right">CGST</th>
                      <th className="px-3 py-2 w-[75px] text-right">SGST</th>
                    </>
                  )}
                  {!form.isExport && form.buyerState && !isIntraState && (
                    <th className="px-3 py-2 w-[75px] text-right">IGST</th>
                  )}
                  <th className="px-3 py-2 w-[90px] text-right">Amount</th>
                  <th className="px-3 py-2 w-[40px]"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => {
                  const calc = calculations.itemCalcs[idx];
                  return (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="px-3 py-2">
                        <input type="text" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)}
                          placeholder="Product or service"
                          className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="text" value={item.hsnSac} onChange={e => updateItem(idx, 'hsnSac', e.target.value)}
                          className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min={0} value={item.quantity} onChange={e => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-right focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-3 py-2">
                        <input type="number" min={0} value={item.rate} onChange={e => updateItem(idx, 'rate', parseFloat(e.target.value) || 0)}
                          className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm text-right focus:border-[var(--ink-0)] focus:outline-none" />
                      </td>
                      <td className="px-3 py-2">
                        <div className="relative">
                          <input type="number" min={0} max={100} step="0.5"
                            value={item.discount || ''} onChange={e => updateItem(idx, 'discount', parseFloat(e.target.value) || 0)}
                            placeholder="0"
                            className={`w-full rounded border px-2 py-1.5 text-center text-sm focus:outline-none ${
                              lineVerdict(item) === 'refused'
                                ? 'border-red-400 bg-red-50 text-red-700 focus:border-red-500'
                                : lineVerdict(item) === 'needs_approval'
                                  ? 'border-amber-400 bg-amber-50 text-amber-800 focus:border-amber-500'
                                  : 'border-gray-200 focus:border-[var(--ink-0)]'
                            }`}
                            title={item.discountApprovalEnabled === false ? 'No approval required' : (item.maxDiscount !== null ? `Max allowed: ${item.maxDiscount}%` : 'No limit set')}
                          />
                          {lineVerdict(item) === 'needs_approval' ? (
                            <span className="absolute -bottom-3.5 left-0 whitespace-nowrap text-[10px] font-medium text-amber-700">needs approval</span>
                          ) : lineVerdict(item) === 'refused' ? (
                            <span className="absolute -bottom-3.5 left-0 whitespace-nowrap text-[10px] font-medium text-red-500">refused · max {item.maxDiscount}%</span>
                          ) : item.maxDiscount !== null && item.discountApprovalEnabled !== false && (
                            <span className="absolute -bottom-3.5 left-0 text-[10px] text-gray-400">max {item.maxDiscount}%</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <select value={item.gstRate} onChange={e => updateItem(idx, 'gstRate', parseFloat(e.target.value))}
                          className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                          {GST_RATES.map(r => <option key={r} value={r}>{r}%</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2">
                        <select value={item.fulfillmentSource} onChange={e => updateItem(idx, 'fulfillmentSource', e.target.value)}
                          className="w-full rounded border border-gray-200 bg-white px-2 py-1.5 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                          {FULFILLMENT_SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        {/* Per-line GST mode chip — amber when differs from doc mode */}
                        {(() => {
                          const lineMode = item.gstMode ?? docGstMode;
                          const differs = lineMode !== docGstMode;
                          return (
                            <button
                              type="button"
                              onClick={() => updateItem(idx, 'gstMode', lineMode === 'extra' ? 'inclusive' : 'extra')}
                              className={`rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                differs
                                  ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-400'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                              }`}
                              title="Click to toggle this line's GST mode"
                            >
                              {lineMode === 'extra' ? '+GST' : 'incl.'}
                            </button>
                          );
                        })()}
                      </td>
                      {!form.isExport && form.buyerState && isIntraState && (
                        <>
                          <td className="px-3 py-2 text-right text-gray-600">{formatINR(calc?.cgst ?? 0)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatINR(calc?.sgst ?? 0)}</td>
                        </>
                      )}
                      {!form.isExport && form.buyerState && !isIntraState && (
                        <td className="px-3 py-2 text-right text-gray-600">{formatINR(calc?.igst ?? 0)}</td>
                      )}
                      <td className="px-3 py-2 text-right font-medium text-gray-900">{formatINR(calc?.total ?? item.quantity * item.rate)}</td>
                      <td className="px-3 py-2">
                        <button onClick={() => removeItem(idx)} disabled={items.length <= 1}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-500 disabled:opacity-30">
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-4 flex justify-end">
            <div className="w-72 space-y-1 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span><span>{formatINR(calculations.subtotal)}</span>
              </div>
              {calculations.totalItemDiscount > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Item Discounts</span><span>-{formatINR(calculations.totalItemDiscount)}</span>
                </div>
              )}
              {calculations.docDiscountAmt > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Order Discount ({calculations.docDiscountPct?.toFixed(1)}%)</span><span>-{formatINR(calculations.docDiscountAmt)}</span>
                </div>
              )}
              {calculations.totalCgst > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>CGST</span><span>{formatINR(calculations.totalCgst)}</span>
                </div>
              )}
              {calculations.totalSgst > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>SGST</span><span>{formatINR(calculations.totalSgst)}</span>
                </div>
              )}
              {calculations.totalIgst > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>IGST</span><span>{formatINR(calculations.totalIgst)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold text-gray-900">
                <span>Grand Total</span><span>{formatINR(calculations.grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Document-Level Discount */}
        <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4">
          <div className="flex items-center gap-3 mb-2">
            <h4 className="text-sm font-semibold text-amber-800">Order Discount</h4>
            <div className="flex rounded-lg border border-amber-300 overflow-hidden">
              <button onClick={() => setDocDiscountType('amount')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'amount' ? 'bg-amber-600 text-white' : 'text-amber-700 hover:bg-amber-100'}`}>
                &#8377; Amount
              </button>
              <button onClick={() => setDocDiscountType('percentage')} className={`px-3 py-1 text-xs font-medium ${docDiscountType === 'percentage' ? 'bg-amber-600 text-white' : 'text-amber-700 hover:bg-amber-100'}`}>
                % Percentage
              </button>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* 🔒 Manak 2026-10-03 — always shown; disabled (naming the category) when a line allows none. */}
            <input type="number" min="0" value={docDiscountValue || ''} onChange={e => setDocDiscountValue(parseFloat(e.target.value) || 0)}
              data-testid="so-order-discount"
              disabled={!!orderDiscountNotAllowed}
              placeholder={docDiscountType === 'amount' ? 'Discount amount' : '% Discount'}
              className="w-48 rounded-lg border border-amber-300 px-3 py-2 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400" />
            <span className="text-sm text-amber-700">
              {docDiscountType === 'amount'
                ? `(${calculations.docDiscountPct?.toFixed(2) || 0}%)`
                : `(${formatINR(calculations.docDiscountAmt || 0)})`}
            </span>
            {!orderDiscountNotAllowed && (calculations.docDiscountPct ?? 0) > Math.min(orderDiscountLimitPct, maxDocDiscountPct) + 1e-9 && (
              <span className="flex items-center gap-1 text-xs text-red-600 font-medium">
                <AlertTriangle size={14} /> Above the {Math.min(orderDiscountLimitPct, maxDocDiscountPct)}% limit — refused, no approver can clear this
              </span>
            )}
          </div>
          <p className="mt-2 text-xs text-amber-700">
            {orderDiscountNotAllowed ?? orderDiscountLimitNote(Math.min(orderDiscountLimitPct, maxDocDiscountPct))}
          </p>
        </div>

        {/* Notes */}
        <div className="rounded-xl border border-gray-200 bg-white p-6">
          <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase tracking-wider">Notes</h2>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Customer Notes</label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Terms & Conditions</label>
              <textarea value={form.terms} onChange={e => setForm({ ...form, terms: e.target.value })} rows={3}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Internal Notes (not visible to customer)</label>
              <textarea value={form.internalNotes} onChange={e => setForm({ ...form, internalNotes: e.target.value })} rows={3}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
          </div>
        </div>

        {/* Bill To / Ship To override — near the order-terms controls */}
        <BillShipOverride
          accountId={form.accountId}
          value={billShip}
          onChange={setBillShip}
          sellerState={sellerState}
        />

        {/* Commodity / trading deal fields — gated by 'commodity_trading' module flag */}
        {showCommodityFields && <div className="rounded-xl border border-[var(--ink-3)] bg-[var(--saffron-50)] p-6">
          <h2 className="mb-4 text-sm font-semibold text-[var(--ink-0)] uppercase tracking-wider">Commodity Deal Details</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-0)]">Commodity</label>
              <input type="text" value={form.commodity} onChange={e => setForm({ ...form, commodity: e.target.value })}
                placeholder="e.g., Rice, Wheat, Sugar"
                className="w-full rounded-lg border border-[var(--ink-3)] bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-0)]">Sales Team Member <span className="text-red-500">*</span></label>
              <input type="text" value={form.salesTeamMember} onChange={e => setForm({ ...form, salesTeamMember: e.target.value })}
                placeholder="Assign sales team member"
                className="w-full rounded-lg border border-[var(--ink-3)] bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-0)]">Phase</label>
              <select value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })}
                className="w-full rounded-lg border border-[var(--ink-3)] bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none">
                <option value="lead">Lead</option>
                <option value="negotiation">Negotiation</option>
                <option value="procurement">Procurement</option>
                <option value="logistics">Logistics</option>
                <option value="execution">Execution</option>
                <option value="payment">Payment</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-0)]">Commodity Cost (₹)</label>
              <input type="number" value={form.commodityCost} onChange={e => setForm({ ...form, commodityCost: e.target.value })}
                placeholder="Purchase cost per MT"
                className="w-full rounded-lg border border-[var(--ink-3)] bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-[var(--ink-0)]">Logistics Cost (₹)</label>
              <input type="number" value={form.logisticsCost} onChange={e => setForm({ ...form, logisticsCost: e.target.value })}
                placeholder="Transport cost per MT"
                className="w-full rounded-lg border border-[var(--ink-3)] bg-white px-3 py-2 text-sm focus:border-[var(--ink-0)] focus:outline-none" />
            </div>
            <div className="flex items-center pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.confirmed} onChange={e => setForm({ ...form, confirmed: e.target.checked })}
                  className="w-4 h-4 rounded border-[var(--ink-3)] text-[var(--ink-0)] focus:ring-[var(--ink-0)]" />
                <span className="text-sm font-medium text-[var(--ink-0)]">Confirmed Deal</span>
              </label>
            </div>
          </div>
        </div>}

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 pb-8">
          <button onClick={() => navigate('/sales-orders')}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={() => handleSave(false)} disabled={saving}
            className="flex items-center gap-2 rounded-lg border border-[var(--ink-3)] bg-[var(--saffron-50)] px-4 py-2 text-sm font-medium text-[var(--ink-0)] hover:bg-[var(--saffron-50)] transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Save as Draft
          </button>
          <button onClick={() => handleSave(true)} disabled={saving}
            className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] transition-colors disabled:opacity-50">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            Save & Confirm
          </button>
        </div>
      </div>

      {/* Product Grid Modal */}
      <ProductGridSelector open={showGrid} onClose={() => setShowGrid(false)} onSelect={handleProductSelect} />
    </div>
  );
}
